import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Search, Send, Zap, StickyNote, Tag, X, RefreshCw,
  MessageCircle, ArrowLeft, Sparkles, User2, Phone as PhoneIcon,
  Check, CheckCheck, AlertTriangle, ChevronRight, Wand2,
  Paperclip, FileText, Info, Loader2, Download,
} from "lucide-react";
import { fmtDateTime24 } from "@/lib/time";

/* WhatsApp Inbox — Phase B.
 *
 * Three-column layout: [conversation list] [thread + composer] [patient meta].
 * All data comes from the /whatsapp/conversations/* endpoints. Realtime is
 * a lightweight `since=<created_at>` poll every 5s — indistinguishable
 * from SSE for receptionist UX and vastly simpler to operate. */

// ─── helpers ──────────────────────────────────────────────────────────────
function fmtRel(iso) {
  if (!iso) return "";
  try {
    const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24); if (d < 7) return `${d}d`;
    return fmtDateTime24(iso).slice(0, 5);
  } catch { return ""; }
}

function StatusTick({ status }) {
  if (status === "read")      return <CheckCheck  className="w-3 h-3 text-sky-500" />;
  if (status === "delivered") return <CheckCheck  className="w-3 h-3 text-ink-soft" />;
  if (status === "sent")      return <Check       className="w-3 h-3 text-ink-soft" />;
  if (status === "failed")    return <AlertTriangle className="w-3 h-3 text-rose-500" />;
  return <span className="w-3 h-3 inline-block rounded-full border border-ink-soft/40" />;
}

// ─── Conversation List (left) ─────────────────────────────────────────────
function ConversationList({ active, onPick, search, setSearch }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/conversations", { params: { search } });
      setRows(data.conversations || []);
    } catch (e) { toast.error(apiError(e, "Failed to load")); }
    finally { setLoading(false); }
  }, [search]);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <aside className="w-full sm:w-[320px] shrink-0 border-r border-[#E7E1D3] bg-white flex flex-col" data-testid="wa-conv-list">
      <div className="p-4 border-b border-[#E7E1D3]">
        <div className="flex items-center gap-2 rounded-full border border-[#E7E1D3] px-3 py-2 focus-within:border-ink transition-colors">
          <Search className="w-3.5 h-3.5 text-ink-soft" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search patient or number"
            className="w-full bg-transparent outline-none text-sm placeholder:text-ink-soft"
            data-testid="wa-conv-search"
          />
          {search && <button onClick={() => setSearch("")} className="text-ink-soft hover:text-ink"><X className="w-3.5 h-3.5" /></button>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="wa-conv-scroll">
        {loading && rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-ink-soft"><RefreshCw className="w-4 h-4 inline animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-ink-soft">No conversations yet</div>
        ) : rows.map((c) => (
          <button key={c.phone} onClick={() => onPick(c.phone)} data-testid={`wa-conv-${c.phone}`}
            className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-[#F3EFE4] transition-colors ${active === c.phone ? "bg-[#FBF7EE]" : "hover:bg-[#FBF7EE]/60"}`}>
            <div className="w-9 h-9 rounded-full bg-[#D06A42]/10 text-[#D06A42] grid place-items-center text-sm font-semibold shrink-0">
              {(c.display_name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-sm text-ink truncate">{c.display_name}</div>
                <div className="text-[10px] text-ink-soft whitespace-nowrap">{fmtRel(c.last_at)}</div>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="text-[12px] text-ink-mid truncate flex-1">
                  {c.last_direction === "outbound" && <StatusTick status={c.last_status} />}
                  <span className="ml-1">{c.last_text || <span className="italic text-ink-soft">no preview</span>}</span>
                </div>
                {c.unread > 0 && (
                  <span className="shrink-0 min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#D06A42] text-white text-[10px] font-semibold grid place-items-center" data-testid={`wa-unread-${c.phone}`}>
                    {c.unread}
                  </span>
                )}
              </div>
              {c.labels?.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {c.labels.slice(0, 2).map((l) => (
                    <span key={l} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E7E1D3] text-ink">{l}</span>
                  ))}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </aside>
  );
}

// ─── Thread (middle) ──────────────────────────────────────────────────────
function Thread({ phone, onOpenMeta }) {
  const [rows, setRows] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [quickReplies, setQR] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const scrollRef = useRef(null);
  const lastAtRef = useRef(null);

  const suggestReplies = useCallback(async () => {
    setAiBusy(true);
    setAiOpen(true);
    try {
      const { data } = await api.post(`/whatsapp/conversations/${encodeURIComponent(phone)}/ai-suggest`);
      setAiSuggestions(data.suggestions || []);
      if (!(data.suggestions || []).length) toast.info("No suggestions available");
    } catch (e) {
      toast.error(apiError(e, "AI suggestion failed"));
      setAiOpen(false);
    } finally { setAiBusy(false); }
  }, [phone]);

  const load = useCallback(async (isPoll = false) => {
    try {
      const { data } = await api.get(`/whatsapp/conversations/${encodeURIComponent(phone)}/messages`,
        { params: isPoll && lastAtRef.current ? { since: lastAtRef.current } : {} });
      if (isPoll) {
        if (data.messages.length) {
          setRows((rs) => [...rs, ...data.messages]);
          lastAtRef.current = data.messages[data.messages.length - 1].created_at;
        }
      } else {
        setRows(data.messages);
        if (data.messages.length) lastAtRef.current = data.messages[data.messages.length - 1].created_at;
      }
    } catch (e) { /* silent on poll */ if (!isPoll) toast.error(apiError(e, "Failed to load thread")); }
  }, [phone]);

  useEffect(() => { setRows([]); lastAtRef.current = null; load(false); }, [phone, load]);
  useEffect(() => { const t = setInterval(() => load(true), 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    // Mark as read on open + reload on unread change.
    api.post(`/whatsapp/conversations/${encodeURIComponent(phone)}/mark-read`).catch(() => {});
  }, [phone]);
  useEffect(() => {
    if (!showQR) return;
    api.get("/whatsapp/quick-replies").then(({ data }) => setQR(data.quick_replies || [])).catch(() => {});
  }, [showQR]);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [rows.length]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    setText("");
    // Optimistic append
    const tempId = `tmp-${Date.now()}`;
    const tempMsg = {
      id: tempId, direction: "outbound", body,
      status: "queued", created_at: new Date().toISOString(),
    };
    setRows((rs) => [...rs, tempMsg]);
    try {
      await api.post(`/whatsapp/conversations/${encodeURIComponent(phone)}/send`, { text: body });
      // Real message will arrive on next poll — drop optimistic
      setTimeout(() => load(false), 800);
    } catch (e) {
      toast.error(apiError(e, "Send failed"));
      setRows((rs) => rs.map((m) => m.id === tempId ? { ...m, status: "failed" } : m));
    } finally { setSending(false); }
  };

  // PDF/document attach flow — upload to Meta media store first (returns
  // media_id), then send a `document` message. PDFs never live on a public
  // URL; Meta serves them from its own store using the media_id.
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [lifecycleWamid, setLifecycleWamid] = useState(null);

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";                       // allow re-pick of same file
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error("File too large — max 20 MB");
      return;
    }
    const allowed = new Set([
      "application/pdf",
      "image/jpeg", "image/png",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/plain", "text/csv",
    ]);
    if (!allowed.has(f.type)) {
      toast.error(`Unsupported type: ${f.type || "unknown"}`);
      return;
    }

    setUploading(true);
    const tempId = `tmp-doc-${Date.now()}`;
    setRows((rs) => [...rs, {
      id: tempId, direction: "outbound",
      body: null, media_type: "file",
      document: { filename: f.name, media_id: "uploading…" },
      status: "queued", created_at: new Date().toISOString(),
    }]);
    try {
      // 1. Upload to Meta
      const fd = new FormData();
      fd.append("file", f);
      const up = await api.post("/whatsapp/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const media_id = up.data.media_id;
      toast.success(`Uploaded to Meta — media_id: ${media_id.slice(0, 12)}…`);

      // 2. Send as document
      const caption = text.trim() || null;
      if (caption) setText("");
      const sr = await api.post("/whatsapp/send/document", {
        to: phone, media_id, filename: f.name, caption,
      });
      if (sr.data?.ok && sr.data?.wamid) {
        toast.success(`Sent · wamid ${sr.data.wamid.slice(0, 18)}…`);
        setLifecycleWamid(sr.data.wamid);
      } else {
        toast.error("Meta rejected — see lifecycle for details");
        if (sr.data?.wamid) setLifecycleWamid(sr.data.wamid);
      }
      setTimeout(() => load(false), 800);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = detail?.meta_error?.message || detail?.message || apiError(err, "Upload failed");
      toast.error(msg);
      setRows((rs) => rs.map((m) => m.id === tempId ? { ...m, status: "failed", error: msg } : m));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-[#FBF7EE]" data-testid="wa-thread">
      {/* header */}
      <div className="px-5 py-3 border-b border-[#E7E1D3] bg-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#D06A42]/10 text-[#D06A42] grid place-items-center text-sm font-semibold">
            {(phone || "?").slice(-2)}
          </div>
          <div>
            <div className="font-display text-base text-ink" data-testid="wa-thread-phone">+{phone}</div>
            <div className="text-[11px] text-ink-soft">WhatsApp Business · session 24h</div>
          </div>
        </div>
        <button onClick={onOpenMeta} className="lg:hidden text-xs text-ink-mid hover:text-ink">
          Details <ChevronRight className="w-3.5 h-3.5 inline" />
        </button>
      </div>

      {/* messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="wa-messages">
        {rows.length === 0 ? (
          <div className="h-full grid place-items-center text-sm text-ink-soft">Loading conversation…</div>
        ) : rows.map((m) => {
          const out = m.direction === "outbound";
          const isDoc = m.media_type === "file" || (m.document && (m.document.media_id || m.document.filename));
          const imageUrl = m.media_url || m.image?.link || m.image?.url || null;
          const isImage = m.media_type === "image" && !!imageUrl;
          const hasBody = !!(m.body && String(m.body).trim());
          const hasContent = hasBody || isDoc || isImage || !!m.template_name;
          return (
            <div key={m.id} className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`wa-msg-${m.id}`}>
              <div className={`max-w-[70%] rounded-2xl px-3.5 py-2 text-sm leading-snug shadow-sm ${out ? "bg-[#D9FDD3] text-ink" : "bg-white text-ink"}`}>
                {isDoc && (
                  <div className="flex items-center gap-2 bg-white/60 border border-black/5 rounded-xl px-2.5 py-2 mb-1"
                       data-testid={`wa-msg-${m.id}-doc`}>
                    <div className="w-9 h-9 rounded-lg bg-rose-100 text-rose-700 grid place-items-center shrink-0">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-ink truncate">
                        {m.document?.filename || "document"}
                      </div>
                      <div className="text-[10px] text-ink-soft truncate">
                        {m.document?.media_id ? `media_id · ${String(m.document.media_id).slice(0, 24)}…` : "attached"}
                      </div>
                    </div>
                  </div>
                )}
                {(hasBody || (!isDoc && !isImage && m.template_name)) && (
                  <div className="whitespace-pre-wrap break-words">
                    {hasBody ? m.body : <em className="text-ink-soft">Template: {m.template_name}</em>}
                  </div>
                )}
                {isImage && (
                  <div className="mb-1" data-testid={`wa-msg-${m.id}-image`}>
                    <a href={imageUrl} target="_blank" rel="noreferrer">
                      <img
                        src={imageUrl}
                        alt={m.caption || "image"}
                        loading="lazy"
                        className="rounded-xl max-w-full max-h-80 object-contain bg-white/40"
                      />
                    </a>
                    {m.caption && (
                      <div className="mt-1 text-[12px] text-ink-mid whitespace-pre-wrap break-words">
                        {m.caption}
                      </div>
                    )}
                  </div>
                )}
                {!hasContent && (
                  <div
                    className={`italic text-[12px] ${m.status === "failed" ? "text-rose-600" : "text-ink-soft"}`}
                    data-testid={`wa-msg-${m.id}-empty`}
                  >
                    {m.status === "failed"
                      ? (m.error || m.error_details?.message || "Send failed")
                      : m.status
                        ? `Status: ${m.status}`
                        : "No content"}
                  </div>
                )}
                <div className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${out ? "text-ink-mid" : "text-ink-soft"}`}>
                  {out && m.provider_msg_id && (
                    <button onClick={() => setLifecycleWamid(m.provider_msg_id)}
                            className="mr-1 opacity-60 hover:opacity-100"
                            title="View delivery lifecycle"
                            data-testid={`wa-lifecycle-open-${m.id}`}>
                      <Info className="w-3 h-3" />
                    </button>
                  )}
                  <span>{fmtDateTime24(m.created_at).slice(-5)}</span>
                  {out && <StatusTick status={m.status} />}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Lifecycle drawer */}
      {lifecycleWamid && (
        <LifecycleDrawer wamid={lifecycleWamid} onClose={() => setLifecycleWamid(null)} />
      )}

      {/* AI reply suggestions panel */}
      {aiOpen && (
        <div className="border-t border-[#E7E1D3] bg-gradient-to-r from-violet-50 to-white px-4 py-3" data-testid="wa-ai-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-violet-700 font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> AI suggestions · Claude Sonnet 4.5
            </div>
            <button onClick={() => setAiOpen(false)} className="text-ink-soft hover:text-ink"><X className="w-3.5 h-3.5" /></button>
          </div>
          {aiBusy ? (
            <div className="flex items-center gap-2 text-xs text-ink-soft py-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Drafting three options…</div>
          ) : aiSuggestions.length === 0 ? (
            <div className="text-xs text-ink-soft italic py-2">No suggestions.</div>
          ) : (
            <div className="space-y-2">
              {aiSuggestions.map((s, i) => (
                <button key={`ai-${i}-${(s || "").slice(0, 32)}`} onClick={() => { setText(s); setAiOpen(false); }}
                  className="w-full text-left text-sm bg-white hover:bg-violet-50 border border-violet-100 hover:border-violet-300 rounded-xl px-3 py-2 text-ink transition-colors"
                  data-testid={`wa-ai-suggestion-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Quick replies panel */}
      {showQR && (
        <div className="border-t border-[#E7E1D3] bg-white px-4 py-2 flex flex-wrap gap-2" data-testid="wa-qr-panel">
          {quickReplies.map((q, i) => (
            <button key={`qr-${i}-${(q || "").slice(0, 24)}`} onClick={() => { setText(q); setShowQR(false); }}
              className="text-xs text-ink bg-[#FBF7EE] border border-[#E7E1D3] px-3 py-1.5 rounded-full hover:border-[#D06A42] transition-colors"
              data-testid={`wa-qr-${i}`}>
              {q}
            </button>
          ))}
        </div>
      )}

      {/* composer */}
      <div className="border-t border-[#E7E1D3] bg-white px-4 py-3">
        <div className="flex items-end gap-2">
          <input ref={fileInputRef} type="file" hidden
                 accept="application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv"
                 onChange={onFileChange} data-testid="wa-file-input" />
          <button onClick={onPickFile} disabled={uploading}
            className={`p-2 rounded-full transition-colors ${uploading ? "bg-amber-100 text-amber-800" : "hover:bg-linen text-ink-mid"} disabled:opacity-50`}
            title="Attach a PDF, image, or document" data-testid="wa-attach-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          </button>
          <button onClick={() => setShowQR((v) => !v)} className={`p-2 rounded-full ${showQR ? "bg-[#D06A42]/10 text-[#D06A42]" : "hover:bg-linen text-ink-mid"}`} title="Quick replies" data-testid="wa-qr-toggle">
            <Zap className="w-4 h-4" />
          </button>
          <button onClick={suggestReplies} disabled={aiBusy}
            className={`p-2 rounded-full transition-colors ${aiOpen ? "bg-violet-100 text-violet-700" : "hover:bg-linen text-ink-mid"} disabled:opacity-50`}
            title="AI reply suggestions" data-testid="wa-ai-toggle">
            {aiBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          </button>
          <textarea
            value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1} placeholder="Type a message… (Enter to send)"
            className="flex-1 resize-none rounded-2xl border border-[#E7E1D3] px-4 py-2.5 text-sm focus:outline-none focus:border-ink"
            data-testid="wa-composer"
          />
          <button onClick={send} disabled={sending || !text.trim()}
            className="p-2.5 rounded-full bg-[#D06A42] text-white disabled:opacity-40 hover:bg-[#E17550] transition-colors"
            data-testid="wa-send">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Meta panel (right) ───────────────────────────────────────────────────
function MetaPanel({ phone, conversation, onLabelsChange }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [labels, setLabels] = useState(conversation?.labels || []);

  const loadNotes = useCallback(async () => {
    try {
      const { data } = await api.get(`/whatsapp/conversations/${encodeURIComponent(phone)}/notes`);
      setNotes(data.notes || []);
    } catch { /* silent */ }
  }, [phone]);
  useEffect(() => { loadNotes(); setLabels(conversation?.labels || []); }, [phone, loadNotes, conversation?.labels]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/whatsapp/conversations/${encodeURIComponent(phone)}/notes`, { body: newNote.trim() });
      setNewNote(""); loadNotes();
    } catch (e) { toast.error(apiError(e, "Add note failed")); }
  };
  const addLabel = async () => {
    const l = newLabel.trim();
    if (!l) return;
    try {
      await api.post(`/whatsapp/conversations/${encodeURIComponent(phone)}/labels`, { label: l });
      const next = [...new Set([...labels, l])];
      setLabels(next); onLabelsChange?.(next); setNewLabel("");
    } catch (e) { toast.error(apiError(e, "Add label failed")); }
  };
  const removeLabel = async (l) => {
    try {
      await api.delete(`/whatsapp/conversations/${encodeURIComponent(phone)}/labels/${encodeURIComponent(l)}`);
      const next = labels.filter((x) => x !== l);
      setLabels(next); onLabelsChange?.(next);
    } catch (e) { toast.error(apiError(e, "Remove failed")); }
  };

  return (
    <aside className="w-full lg:w-[320px] shrink-0 border-l border-[#E7E1D3] bg-white overflow-y-auto" data-testid="wa-meta">
      <div className="p-5 border-b border-[#E7E1D3]">
        <div className="w-14 h-14 rounded-full bg-[#D06A42]/10 text-[#D06A42] grid place-items-center text-xl font-semibold mx-auto">
          {(conversation?.display_name || phone || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="mt-3 text-center">
          <div className="font-display text-lg text-ink">{conversation?.display_name || phone}</div>
          <div className="text-[12px] text-ink-mid mt-0.5 flex items-center justify-center gap-1">
            <PhoneIcon className="w-3 h-3" /> +{phone}
          </div>
          {conversation?.patient_id && (
            <Link to={`/app/patients/${conversation.patient_id}`}
              className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#D06A42] hover:underline"
              data-testid="wa-open-patient">
              Open patient profile <ChevronRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>

      {/* Labels */}
      <div className="p-5 border-b border-[#E7E1D3]">
        <div className="flex items-center gap-2 mb-2">
          <Tag className="w-3.5 h-3.5 text-ink-mid" />
          <div className="text-[11px] uppercase tracking-widest text-ink-soft">Labels</div>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {labels.length === 0 && <div className="text-xs text-ink-soft italic">None yet</div>}
          {labels.map((l) => (
            <span key={l} className="inline-flex items-center gap-1 text-xs bg-[#FBF7EE] border border-[#E7E1D3] rounded-full pl-2.5 pr-1 py-0.5" data-testid={`wa-label-${l}`}>
              {l}
              <button onClick={() => removeLabel(l)} className="text-ink-soft hover:text-rose-600"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-1">
          <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addLabel()}
            placeholder="Add label…" data-testid="wa-add-label-input"
            className="flex-1 text-xs border border-[#E7E1D3] rounded-full px-3 py-1.5 focus:outline-none focus:border-ink" />
          <button onClick={addLabel} className="text-xs font-medium text-[#D06A42] px-2" data-testid="wa-add-label">Add</button>
        </div>
      </div>

      {/* Notes */}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <StickyNote className="w-3.5 h-3.5 text-ink-mid" />
          <div className="text-[11px] uppercase tracking-widest text-ink-soft">Internal notes</div>
        </div>
        <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)}
          rows={2} placeholder="Add a note (visible to staff only)…"
          className="w-full text-sm border border-[#E7E1D3] rounded-xl px-3 py-2 focus:outline-none focus:border-ink resize-none"
          data-testid="wa-note-input" />
        <button onClick={addNote} disabled={!newNote.trim()}
          className="mt-2 text-xs font-semibold text-white bg-[#D06A42] hover:bg-[#E17550] disabled:opacity-40 px-3 py-1.5 rounded-full"
          data-testid="wa-note-add">Add note</button>
        <div className="mt-4 space-y-2">
          {notes.length === 0 && <div className="text-xs text-ink-soft italic">No notes yet</div>}
          {notes.slice().reverse().map((n) => (
            <div key={n.id} className="text-xs bg-[#FBF7EE] rounded-xl p-2.5" data-testid={`wa-note-${n.id}`}>
              <div className="text-ink whitespace-pre-wrap">{n.body}</div>
              <div className="mt-1 text-[10px] text-ink-soft">{n.author_name} · {fmtRel(n.created_at)} ago</div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────
export default function WhatsAppInbox() {
  const { phone: routePhone } = useParams();
  const nav = useNavigate();
  const [active, setActive] = useState(routePhone || null);
  const [search, setSearch] = useState("");
  const [convs, setConvs] = useState([]);
  const [mobileMetaOpen, setMobileMetaOpen] = useState(false);

  // Keep the conversation summary for the currently-selected phone (used by
  // MetaPanel for name + labels). Cheap client-side lookup only.
  const conversation = useMemo(
    () => convs.find((c) => c.phone === active),
    [convs, active],
  );

  // Poll conversation list every 5s to keep unread badges fresh.
  const loadConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/conversations");
      setConvs(data.conversations || []);
      if (!active && (data.conversations || []).length > 0) {
        setActive(data.conversations[0].phone);
      }
    } catch { /* silent */ }
  }, [active]);
  useEffect(() => { loadConvs(); const t = setInterval(loadConvs, 5000); return () => clearInterval(t); }, [loadConvs]);

  const pick = (p) => { setActive(p); nav(`/app/whatsapp/inbox/${p}`, { replace: true }); };

  return (
    <div className="fixed inset-y-0 right-0 left-[var(--sidebar-w,220px)] flex flex-col bg-white" data-testid="wa-inbox-page">
      <div className="border-b border-[#E7E1D3] bg-white px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/app/whatsapp" className="inline-flex items-center gap-1.5 text-xs text-ink-mid hover:text-ink" data-testid="wa-inbox-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
          <div className="font-display text-lg text-ink flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[#D06A42]" /> Inbox
          </div>
        </div>
        <div className="text-[11px] text-ink-soft">Live · updates every 5s</div>
      </div>
      <div className="flex-1 flex overflow-hidden">
        <ConversationList active={active} onPick={pick} search={search} setSearch={setSearch} />
        {active ? (
          <>
            <Thread phone={active} onOpenMeta={() => setMobileMetaOpen(true)} />
            <div className={`${mobileMetaOpen ? "block" : "hidden"} lg:block`}>
              <MetaPanel phone={active} conversation={conversation}
                onLabelsChange={(labels) => setConvs((cs) => cs.map((c) => c.phone === active ? { ...c, labels } : c))} />
            </div>
          </>
        ) : (
          <div className="flex-1 grid place-items-center text-sm text-ink-soft">Pick a conversation to start</div>
        )}
      </div>
    </div>
  );
}

// ─── Lifecycle drawer — the single-message drilldown ──────────────────────
// Fetches the FULL server-side lifecycle for one WAMID and renders:
//   API accepted → sent → delivered → read
// or
//   API accepted → failed → exact Meta error object
function LifecycleDrawer({ wamid, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/whatsapp/message/lifecycle/${encodeURIComponent(wamid)}`);
      setData(data);
    } catch (e) {
      setData({ error: apiError(e, "not found"), raw: e?.response?.data });
    } finally { setBusy(false); }
  }, [wamid]);

  useEffect(() => { load(); }, [load]);

  const StageChip = ({ label, reached, tone }) => (
    <div className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-3 py-1.5 rounded-full border ${
      reached
        ? (tone === "rose"
            ? "bg-rose-50 border-rose-200 text-rose-800"
            : "bg-emerald-50 border-emerald-200 text-emerald-800")
        : "bg-stone-50 border-stone-200 text-stone-400"}`}>
      {reached && (tone === "rose" ? <AlertTriangle className="w-3 h-3" /> : <Check className="w-3 h-3" />)}
      {label}
    </div>
  );

  const reached = data?.reached || {};
  const err = data?.error_details;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}
         data-testid="wa-lifecycle-drawer">
      <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#E7E1D3] px-5 py-3 flex items-center justify-between z-10">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-ink-mid">Delivery lifecycle</div>
            <div className="font-mono text-xs text-ink mt-0.5 truncate max-w-[380px]" title={wamid}>{wamid}</div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={load} className="p-1.5 hover:bg-linen rounded-full text-ink-mid" title="Refresh" data-testid="wa-lifecycle-refresh">
              <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-linen rounded-full text-ink-mid" data-testid="wa-lifecycle-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {busy && !data ? (
            <div className="flex items-center gap-2 text-sm text-ink-mid"><Loader2 className="w-4 h-4 animate-spin" /> Loading lifecycle…</div>
          ) : data?.error ? (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3">{data.error}</div>
          ) : data ? (
            <>
              {/* Progress rail */}
              <div>
                <div className="text-[11px] uppercase tracking-widest text-ink-mid mb-2">Stages</div>
                <div className="flex flex-wrap gap-2" data-testid="wa-lifecycle-stages">
                  <StageChip label="API accepted"  reached={!!data.wamid} />
                  <StageChip label="sent"          reached={reached.sent} />
                  <StageChip label="delivered"     reached={reached.delivered} />
                  <StageChip label="read"          reached={reached.read} />
                  {reached.failed && <StageChip label="failed" reached tone="rose" />}
                </div>
                {!reached.delivered && !reached.failed && (
                  <p className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    Meta accepted the send (returned wamid) but no delivery status has arrived.
                    If this persists &gt; 60s, check: 24-hour session window · webhook `messages` field subscribed · META_APP_SECRET matches.
                  </p>
                )}
              </div>

              {/* Meta error */}
              {err && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 space-y-1.5" data-testid="wa-lifecycle-error">
                  <div className="text-[11px] uppercase tracking-widest text-rose-900 font-semibold">Meta error</div>
                  <div className="text-sm text-ink"><b>Code:</b> {err.code} <span className="text-ink-mid">·</span> <b>Title:</b> {err.title}</div>
                  <div className="text-sm text-ink"><b>Message:</b> {err.message}</div>
                  {err.error_data?.details && <div className="text-sm text-ink-mid"><b>Details:</b> {err.error_data.details}</div>}
                  {err.href && <a href={err.href} target="_blank" rel="noreferrer" className="text-xs text-rose-700 underline">Meta docs →</a>}
                </div>
              )}

              {/* Facts grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <FactRow k="Recipient"       v={data.recipient} mono />
                <FactRow k="Current status"  v={data.current_status} />
                <FactRow k="Type"            v={data.message_type} />
                <FactRow k="Template"        v={data.template_name || "—"} />
                <FactRow k="Created"         v={data.created_at} mono />
                <FactRow k="Graph version"   v={data.graph_version} mono />
              </div>

              {/* Document metadata */}
              {data.document && (
                <div className="rounded-2xl border border-[#E7E1D3] bg-linen/60 p-4 text-sm space-y-1">
                  <div className="text-[11px] uppercase tracking-widest text-ink-mid mb-1">Document</div>
                  <div><b>Filename:</b> {data.document.filename}</div>
                  <div className="font-mono text-xs break-all"><b>Media ID:</b> {data.document.media_id}</div>
                  {data.document.caption && <div><b>Caption:</b> {data.document.caption}</div>}
                </div>
              )}

              {/* Raw payloads */}
              <details className="rounded-xl bg-stone-50 border border-[#E7E1D3] px-3 py-2">
                <summary className="text-[11px] uppercase tracking-widest text-ink-mid cursor-pointer">Request sent to Meta</summary>
                <pre className="mt-2 text-[10px] text-ink-mid overflow-x-auto max-h-56 whitespace-pre-wrap">{JSON.stringify(data.request_sent_to_meta, null, 2)}</pre>
              </details>
              <details className="rounded-xl bg-stone-50 border border-[#E7E1D3] px-3 py-2">
                <summary className="text-[11px] uppercase tracking-widest text-ink-mid cursor-pointer">Response from Meta</summary>
                <pre className="mt-2 text-[10px] text-ink-mid overflow-x-auto max-h-56 whitespace-pre-wrap">{JSON.stringify(data.response_from_meta, null, 2)}</pre>
              </details>
              <details className="rounded-xl bg-stone-50 border border-[#E7E1D3] px-3 py-2">
                <summary className="text-[11px] uppercase tracking-widest text-ink-mid cursor-pointer">Status history ({(data.status_history || []).length})</summary>
                <pre className="mt-2 text-[10px] text-ink-mid overflow-x-auto max-h-56 whitespace-pre-wrap">{JSON.stringify(data.status_history, null, 2)}</pre>
              </details>
              <p className="text-[11px] text-ink-mid leading-relaxed italic">{data.operator_note}</p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function FactRow({ k, v, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ink-mid">{k}</div>
      <div className={`mt-0.5 text-sm text-ink ${mono ? "font-mono" : ""} truncate`} title={String(v)}>{v ?? "—"}</div>
    </div>
  );
}

